import type {
    LineChartGeometryConstantType,
    LineChartXLabelsConstantType,
    LineChartLabelsConstantType,
    LineChartMatrixConstantType,
    LineChartExtentConstantType,
    LineChartSpreadConstantType,
    LineChartPlotConstantType,
    LineChartEndsConstantType,
    LineChartColorParts,
    LineChartGeometry,
    LineChartXLabel,
    LineChartMatrix,
    LineChartLabel,
    LineChartLayer,
    LineChartEnd
} from "./type";
import {
    LINE_CHART_LABEL_BASELINE_SHIFT
} from "./stylesheet";
import type {
    ChartPoint,
    ChartRect
} from "../chart/type";
import {
    getChartLabelIndexes,
    getChartLinearScale,
    getChartTextWidth,
    getChartPlotRect,
    getChartPointX
} from "../chart/util";

const LINE_CHART_COLOR_PATTERN = /rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)\s*(?:,\s*([\d.]+)\s*)?\)/;

export const getLineChartMatrix = ({
    seriesKeys,
    data
}: LineChartMatrixConstantType): LineChartMatrix => {
    const values: Array<number> = [];

    const mask: Array<boolean> = [];

    seriesKeys.forEach((key) => {
        data.forEach((row) => {
            const raw = row[key];

            const value = raw === null || raw === undefined || raw === "" ? NaN : Number(raw);

            const isDefined = Number.isFinite(value);

            values.push(isDefined ? value : 0);

            mask.push(isDefined);
        });
    });

    return {
        values,
        mask
    };
};

export const getLineChartExtent = ({
    stackBehaviour,
    visibleIndexes,
    matrix,
    count
}: LineChartExtentConstantType) => {
    if(stackBehaviour === "expanded") {
        return {
            max: 100,
            min: 0
        };
    }

    const list: Array<number> = [];

    if(stackBehaviour === "stacked") {
        for(let index = 0; index < count; index += 1) {
            list.push(visibleIndexes.reduce((total, seriesIndex) => total + (matrix.values[seriesIndex * count + index] ?? 0), 0));
        }
    } else {
        visibleIndexes.forEach((seriesIndex) => {
            for(let index = 0; index < count; index += 1) {
                if(matrix.mask[seriesIndex * count + index]) {
                    list.push(matrix.values[seriesIndex * count + index] ?? 0);
                }
            }
        });
    }

    return {
        max: Math.max(0, ...list),
        min: Math.min(0, ...list)
    };
};

export const getLineChartPlot = ({
    endReserve,
    yAxisWidth,
    measures,
    height,
    isRTL,
    width
}: LineChartPlotConstantType): ChartRect => {
    const {
        typography,
        spacingSm,
        spacingXs
    } = measures;

    const base = getChartPlotRect({
        xAxisHeight: typography.lineHeight + spacingSm,
        inset: typography.lineHeight + spacingXs,
        yAxisWidth,
        height,
        isRTL,
        width
    });

    const reserve = Math.min(endReserve, base.width);

    return {
        ...base,
        x: isRTL ? base.x + reserve : base.x,
        width: base.width - reserve
    };
};

export const getLineChartGeometry = ({
    weights,
    values,
    isRTL,
    shape,
    plot
}: LineChartGeometryConstantType): LineChartGeometry => {
    const {
        stackBehaviour,
        seriesKeys,
        count,
        mask
    } = shape;

    const seriesCount = seriesKeys.length;

    const domainMin = values[seriesCount * count] ?? 0;

    const domainMax = values[seriesCount * count + 1] ?? 1;

    const toValue = getChartLinearScale({
        range: [
            plot.y + plot.height,
            plot.y
        ],
        domain: [
            domainMin,
            domainMax
        ]
    });

    const baseline = toValue(0);

    const xs = Array.from({
        length: count
    }, (_, index) => getChartPointX({
        size: plot.width,
        start: plot.x,
        index,
        isRTL,
        count
    }));

    const isStacked = stackBehaviour !== "overlay";

    const getValue = (seriesIndex: number, index: number): number => (values[seriesIndex * count + index] ?? 0) * (weights[seriesIndex] ?? 1);

    const totals = xs.map((_, index) => seriesKeys.reduce((total, __, seriesIndex) => total + getValue(seriesIndex, index), 0));

    const below = xs.map(() => 0);

    const layers = seriesKeys.map((_, seriesIndex): LineChartLayer => {
        const bottomPoints: Array<ChartPoint | null> = xs.map((x, index) => ({
            y: toValue(below[index] ?? 0),
            x
        }));

        const top = xs.map((x, index): ChartPoint | null => {
            if(!isStacked) {
                return mask[seriesIndex * count + index] ? {
                    y: toValue(values[seriesIndex * count + index] ?? 0),
                    x
                } : null;
            }

            const value = getValue(seriesIndex, index);

            const share = stackBehaviour === "expanded" ? (totals[index] ? value / (totals[index] ?? 1) * domainMax : 0) : value;

            below[index] = (below[index] ?? 0) + share;

            return {
                y: toValue(below[index] ?? 0),
                x
            };
        });

        return {
            bottom: isStacked && seriesIndex > 0 ? bottomPoints : baseline,
            top
        };
    });

    return {
        baseline,
        toValue,
        layers,
        xs
    };
};

export const getLineChartColorParts = (color: string): LineChartColorParts => {
    const match = LINE_CHART_COLOR_PATTERN.exec(color);

    if(!match) {
        return {
            opacity: 1,
            color
        };
    }

    return {
        opacity: match[4] === undefined ? 1 : Number(match[4]),
        color: `rgb(${match[1]},${match[2]},${match[3]})`
    };
};

export const getLineChartXLabels = ({
    categories,
    isRTL,
    plot
}: LineChartXLabelsConstantType): Array<LineChartXLabel> => {
    const count = categories.length;

    const last = count - 1;

    return Array.from({
        length: count
    }, (_, index): LineChartXLabel => {
        const isFirst = index === 0 && count > 1;

        const isLast = index === last && count > 1;

        const isStart = isRTL ? isLast : isFirst;

        const isEnd = isRTL ? isFirst : isLast;

        return {
            anchor: isEnd ? "end" : isStart ? "start" : "middle",
            text: categories[index] ?? "",
            x: getChartPointX({
                size: plot.width,
                start: plot.x,
                index,
                isRTL,
                count
            }),
            index
        };
    });
};

export const getLineChartSpread = ({
    items,
    max,
    min,
    gap
}: LineChartSpreadConstantType): Record<string, number> => {
    const place = (group: { list: Array<{ key: string; y: number }>; center: number }) => {
        const span = (group.list.length - 1) * gap;

        return Math.max(min, Math.min(max - span, group.center - span / 2));
    };

    const groups = [
        ...items
    ].sort((first, second) => first.y - second.y).map((item) => ({
        center: item.y,
        list: [
            item
        ]
    }));

    let isMerged = true;

    while(isMerged) {
        isMerged = false;

        for(let index = 0; index < groups.length - 1; index += 1) {
            const first = groups[index];

            const second = groups[index + 1];

            if(!first || !second) {
                continue;
            }

            if(place(second) - (place(first) + (first.list.length - 1) * gap) < gap) {
                const list = [
                    ...first.list,
                    ...second.list
                ];

                groups.splice(index, 2, {
                    center: list.reduce((total, item) => total + item.y, 0) / list.length,
                    list
                });

                isMerged = true;

                break;
            }
        }
    }

    const output: Record<string, number> = {};

    groups.forEach((group) => {
        const top = place(group);

        group.list.forEach((item, position) => {
            output[item.key] = top + position * gap;
        });
    });

    return output;
};

export const getLineChartEnds = ({
    endLabelBehaviour,
    measures,
    isRTL,
    items,
    plot
}: LineChartEndsConstantType): Array<LineChartEnd> => {
    if(endLabelBehaviour === "none" || !items.length) {
        return [];
    }

    const {
        typography,
        dotRadius,
        spacingSm,
        spacingXs,
        haloWidth
    } = measures;

    const direction = isRTL ? -1 : 1;

    const reach = dotRadius + haloWidth;

    const anchor = isRTL ? "end" : "start";

    if(endLabelBehaviour === "label") {
        const placed: Array<LineChartEnd> = [];

        items.forEach((item) => {
            const end: LineChartEnd = {
                y: item.point.y + typography.fontSize * LINE_CHART_LABEL_BASELINE_SHIFT,
                x: item.point.x + direction * (reach + spacingXs),
                opacity: item.opacity,
                point: item.point,
                color: item.color,
                text: item.text,
                key: item.key,
                anchor
            };

            const isOverlapping = placed.some((other) => Math.abs(other.y - end.y) < typography.lineHeight);

            if(!isOverlapping) {
                placed.push(end);
            }
        });

        return placed;
    }

    const spread = getLineChartSpread({
        items: items.map((item) => ({
            y: item.point.y,
            key: item.key
        })),
        gap: typography.lineHeight,
        max: plot.y + plot.height,
        min: plot.y
    });

    return items.map((item): LineChartEnd => {
        const targetY = spread[item.key] ?? item.point.y;

        const startX = item.point.x + direction * reach;

        const bendX = startX + direction * spacingSm;

        const endX = bendX + direction * spacingSm;

        return {
            lead: `M${startX},${item.point.y}L${bendX},${targetY}L${endX},${targetY}`,
            y: targetY + typography.fontSize * LINE_CHART_LABEL_BASELINE_SHIFT,
            x: endX + direction * spacingXs,
            opacity: item.opacity,
            point: item.point,
            color: item.color,
            text: item.text,
            key: item.key,
            anchor
        };
    });
};

export const getLineChartLabels = ({
    labelBehaviour,
    valueFormatter,
    isEndLabeled,
    measures,
    series,
    isRTL,
    plot
}: LineChartLabelsConstantType): Array<LineChartLabel> => {
    const labels: Array<LineChartLabel> = [];

    const {
        typography,
        spacingSm,
        spacingXs
    } = measures;

    const {
        lineHeight,
        fontSize
    } = typography;

    series.forEach((item) => {
        const definedIndexes = item.top.map((_, index) => index).filter((index) => item.mask[index] && item.top[index]);

        const lastIndex = item.top.length - 1;

        const indexes = getChartLabelIndexes({
            values: definedIndexes.map((index) => item.values[index] ?? 0),
            behaviour: labelBehaviour
        }).map((position) => definedIndexes[position]).filter((index) => !isEndLabeled || index !== lastIndex);

        const definedValues = definedIndexes.map((index) => item.values[index] ?? 0);

        const lowest = definedIndexes[definedValues.indexOf(Math.min(...definedValues))];

        const highest = definedIndexes[definedValues.indexOf(Math.max(...definedValues))];

        indexes.forEach((index) => {
            const point = index === undefined ? undefined : item.top[index];

            if(index === undefined || !point) {
                return;
            }

            const text = valueFormatter(item.values[index] ?? 0);

            const isEdgeStart = isRTL ? index === lastIndex : index === 0;

            const isEdgeEnd = isRTL ? index === 0 : index === lastIndex;

            const anchor = isEdgeEnd && lastIndex > 0 ? "end" : isEdgeStart && lastIndex > 0 ? "start" : "middle";

            const isBelow = labelBehaviour === "extremes" && index === lowest && index !== highest && point.y + lineHeight + spacingXs <= plot.y + plot.height;

            const label: LineChartLabel = {
                y: isBelow ? point.y + lineHeight + spacingXs : point.y - spacingSm - fontSize * LINE_CHART_LABEL_BASELINE_SHIFT,
                x: point.x,
                anchor,
                text
            };

            const getLeft = (target: LineChartLabel) => {
                const width = getChartTextWidth({
                    text: target.text,
                    fontSize
                });

                return target.anchor === "start" ? target.x : target.anchor === "end" ? target.x - width : target.x - width / 2;
            };

            const left = getLeft(label);

            const width = getChartTextWidth({
                fontSize,
                text
            });

            const isOverlapping = labels.some((other) => {
                const otherLeft = getLeft(other);

                const otherWidth = getChartTextWidth({
                    text: other.text,
                    fontSize
                });

                return Math.abs(other.y - label.y) < lineHeight && left < otherLeft + otherWidth && otherLeft < left + width;
            });

            if(!isOverlapping) {
                labels.push(label);
            }
        });
    });

    return labels;
};
